/**
 * Les règles du suivi de flotte, écrites UNE fois.
 *
 * « Où sont mes camions » et ses sous-composants (carte, liste, fenêtre d'un
 * camion, compteurs) décidaient chacun de leur côté ce qu'est un camion « en
 * mouvement » (une fois selon l'étape du trajet, une autre selon la vitesse) et
 * ce qu'est un « signal actif » (un drapeau du serveur que le navigateur
 * recopiait sans jamais le revérifier). Tout passe maintenant par ici.
 */
import { LIBELLES } from '../../lib/libelles';
import { enDate, formatDate, formatDateHeure, formatIlYA } from '../../lib/formats';

/**
 * Au-delà de cet âge, une position n'est plus « en direct ».
 * Miroir de `STALE_AFTER_SECONDS` dans backend/app/services/tracking_resolver.py
 * (un test de chaque côté fige la même valeur).
 */
export const SIGNAL_MAX_AGE_SECONDS = 5 * 60;

/** En dessous de cette vitesse, un camion est à l'arrêt (bruit du GPS compris). */
export const MOUVEMENT_VITESSE_MIN_KMH = 3;

/** Textes du signal, écrits ici et nulle part ailleurs. */
export const SIGNAL_LABELS = { on: 'Signal actif', off: 'Sans signal' };

const MS = 1000;

/**
 * Clé d'un camion dans la flotte. Une ligne sans camion identifié (le serveur la
 * range sous son trajet) reste sélectionnable : la clé retombe sur le trajet.
 */
export const cleVehicule = (vehicule) => {
  if (!vehicule) return null;
  if (vehicule.truck_id) return vehicule.truck_id;
  return vehicule.trip_id ? `trip:${vehicule.trip_id}` : null;
};

const nombre = (valeur) => {
  if (valeur === null || valeur === undefined || valeur === '') return null;
  const n = Number(valeur);
  return Number.isFinite(n) ? n : null;
};

/** Une position affichable sur la carte (0 est une coordonnée valable). */
export const aUnePosition = (vehicule) =>
  !!vehicule && nombre(vehicule.latitude) !== null && nombre(vehicule.longitude) !== null;

/** Âge de la position en secondes, ou null si on ne sait pas la dater. */
export const ageSecondes = (vehicule, maintenant = Date.now()) => {
  const date = enDate(vehicule?.timestamp);
  if (!date) return null;
  return (maintenant - date.getTime()) / MS;
};

/**
 * LA règle du signal : la dernière position date de moins de cinq minutes.
 * Un horodatage inconnu n'est pas un signal. Un petit décalage d'horloge entre
 * le serveur et le poste (position « dans le futur ») ne coupe pas le signal.
 */
export const estPositionFraiche = (vehicule, maintenant = Date.now()) => {
  const age = ageSecondes(vehicule, maintenant);
  return age !== null && age <= SIGNAL_MAX_AGE_SECONDS && age > -SIGNAL_MAX_AGE_SECONDS;
};
export const aSignal = estPositionFraiche;

/** Vitesse en km/h, ou null quand elle est inconnue (et non « 0 »). */
export const vitesseKmh = (vehicule) => nombre(vehicule?.speed_kmh);

/** LA règle du mouvement : roule à plus de 3 km/h ET la position est fraîche. */
export const estEnMouvement = (vehicule, maintenant = Date.now()) => {
  const vitesse = vitesseKmh(vehicule);
  return vitesse !== null && vitesse > MOUVEMENT_VITESSE_MIN_KMH && estPositionFraiche(vehicule, maintenant);
};

export const vitesseTexte = (vehicule) => {
  const vitesse = vitesseKmh(vehicule);
  return vitesse === null ? '—' : `${Math.round(vitesse)} km/h`;
};

/** « il y a 4 min », « il y a 2 jours » ; null si la position n'est pas datée. */
export const ageTexte = (vehicule, maintenant = Date.now()) => {
  const texte = formatIlYA(vehicule?.timestamp, '', maintenant);
  if (!texte) return null;
  return texte.replace(/(\d+) j$/, (_, n) => `${n} ${n === '1' ? 'jour' : 'jours'}`);
};

/** Lien carte des coordonnées, à envoyer à un chauffeur ou un dépanneur. */
export const lienPosition = (vehicule) =>
  aUnePosition(vehicule)
    ? `https://www.google.com/maps?q=${Number(vehicule.latitude)},${Number(vehicule.longitude)}`
    : null;

/** La date et l'heure exactes, pour l'infobulle. */
export const dateExacteTexte = (vehicule) => formatDateHeure(vehicule?.timestamp, '') || formatDate(vehicule?.timestamp, '');

/* ------------------------------------------------------------------ */
/* Durée d'arrêt                                                        */
/* ------------------------------------------------------------------ */

/** Au-delà de cette durée, un arrêt est « long » (filtre, couleur). */
export const ARRET_LONG_SECONDES = 2 * 3600;

/** Le serveur ne remonte pas plus loin (STOP_LOOKBACK, backend tracking.py). */
export const ARRET_RECUL_MAX_SECONDES = 48 * 3600;

/**
 * Depuis combien de secondes le camion est immobile, ou null.
 * Il faut une position fraîche (un camion muet n'est pas « à l'arrêt », on ne
 * sait pas), une vitesse connue sous le seuil, et le départ de l'arrêt
 * (`stopped_since`, calculé par le serveur puis tenu à jour par le flux).
 */
export const arretDepuisSecondes = (vehicule, maintenant = Date.now()) => {
  if (!estPositionFraiche(vehicule, maintenant)) return null;
  const vitesse = vitesseKmh(vehicule);
  if (vitesse === null || vitesse > MOUVEMENT_VITESSE_MIN_KMH) return null;
  const depart = enDate(vehicule?.stopped_since);
  if (!depart) return null;
  return Math.max(0, (maintenant - depart.getTime()) / MS);
};

export const estArretLong = (vehicule, maintenant = Date.now()) =>
  (arretDepuisSecondes(vehicule, maintenant) ?? 0) >= ARRET_LONG_SECONDES;

/** « 12 min », « 2 h 15 », « 5 h », « plus de 48 h » ; null sans durée. */
export const dureeArretTexte = (secondes) => {
  if (secondes === null || secondes === undefined || !Number.isFinite(secondes)) return null;
  if (secondes >= ARRET_RECUL_MAX_SECONDES - 30 * 60) return 'plus de 48 h';
  const minutes = Math.floor(secondes / 60);
  if (minutes < 1) return 'moins d’1 min';
  if (minutes < 60) return `${minutes} min`;
  const heures = Math.floor(minutes / 60);
  const reste = minutes % 60;
  return reste ? `${heures} h ${String(reste).padStart(2, '0')}` : `${heures} h`;
};

/**
 * Compteurs de l'en-tête : une seule définition pour toute la flotte.
 * « En mouvement » et « à l'arrêt » ne se comptent que parmi les camions qui
 * donnent leur position : un camion muet n'est ni l'un ni l'autre.
 */
export const compterFlotte = (vehicules, maintenant = Date.now()) => {
  const liste = Array.isArray(vehicules) ? vehicules : [];
  let enLigne = 0;
  let enMouvement = 0;
  let arretsLongs = 0;
  liste.forEach((vehicule) => {
    if (!estPositionFraiche(vehicule, maintenant)) return;
    enLigne += 1;
    if (estEnMouvement(vehicule, maintenant)) enMouvement += 1;
    if (estArretLong(vehicule, maintenant)) arretsLongs += 1;
  });
  return {
    total: liste.length,
    enLigne,
    horsLigne: liste.length - enLigne,
    enMouvement,
    aLArret: enLigne - enMouvement,
    arretsLongs,
  };
};

/* ------------------------------------------------------------------ */
/* Origine de la position                                               */
/* ------------------------------------------------------------------ */

/**
 * D'où vient la position, dit simplement (domaine `gpsSource` de
 * lib/libelles.js, le même que celui du serveur). Une source absente ou
 * inconnue ne donne rien : on n'écrit pas « origine inconnue ».
 */
export const libelleSource = (source) => {
  if (!source) return '';
  return LIBELLES.gpsSource?.[String(source).trim().toLowerCase()] || '';
};

/**
 * Le boîtier a cessé de répondre et le téléphone du chauffeur a pris le relais.
 * Le serveur ne lève `fallback_used` que si la position retenue est celle du
 * téléphone alors qu'un autre appareil avait aussi parlé.
 */
export const estPositionDeSecours = (vehicule) =>
  !!vehicule?.fallback_used && String(vehicule?.source || '') === 'driver_app';

export const TEXTE_SECOURS = 'Position du téléphone (le boîtier ne répond plus)';
export const TEXTE_CONFLIT = 'Positions contradictoires';

/** Catégories de l'écran « origine » : le filtre et l'étiquette partagent ce vocabulaire. */
export const ORIGINES = {
  conflit: TEXTE_CONFLIT,
  secours: TEXTE_SECOURS,
  boitier: 'Boîtier GPS',
  fournisseur: 'Fournisseur GPS',
  telephone: 'Téléphone du chauffeur',
};

export const origineDe = (vehicule) => {
  if (vehicule?.tracking_conflict) return 'conflit';
  if (estPositionDeSecours(vehicule)) return 'secours';
  const libelleDeSource = libelleSource(vehicule?.source);
  if (libelleDeSource === ORIGINES.boitier) return 'boitier';
  if (libelleDeSource === ORIGINES.fournisseur) return 'fournisseur';
  if (libelleDeSource === ORIGINES.telephone) return 'telephone';
  return null;
};

/* ------------------------------------------------------------------ */
/* Fiabilité                                                            */
/* ------------------------------------------------------------------ */

export const FIABILITE_BONNE_MIN = 70;
export const FIABILITE_MOYENNE_MIN = 40;

/** Le score de qualité dit en mots ; le pourcentage reste pour l'infobulle. */
export const niveauFiabilite = (score) => {
  const valeur = nombre(score);
  if (valeur === null) return null;
  const pourcentage = Math.round(Math.max(0, Math.min(100, valeur)));
  if (valeur >= FIABILITE_BONNE_MIN) return { niveau: 'bonne', libelle: 'Bonne', pourcentage };
  if (valeur >= FIABILITE_MOYENNE_MIN) return { niveau: 'moyenne', libelle: 'Moyenne', pourcentage };
  return { niveau: 'faible', libelle: 'Faible', pourcentage };
};

/* ------------------------------------------------------------------ */
/* Texte d'accessibilité                                                */
/* ------------------------------------------------------------------ */

/** « AA-123 — En route, signal actif, il y a 2 min » : titre et nom accessible du marqueur. */
export const descriptionMarqueur = (plaque, libelleFamille, signal, age) =>
  [`${plaque || 'Camion sans plaque'} — ${libelleFamille}`, signal ? SIGNAL_LABELS.on.toLowerCase() : SIGNAL_LABELS.off.toLowerCase(), age]
    .filter(Boolean)
    .join(', ');
